import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { ThemeContext } from "../context/ThemeContext";
import { useApi } from '../hooks/useApi';
import { FormAlert, FormShell, FormField, FormActions, formInputClass } from '../components/form';
import CountrySubdivisionSelect from '../components/form/CountrySubdivisionSelect';
import PhoneCountryInput from '../components/form/PhoneCountryInput';
import { useThemeClasses } from '../hooks/useThemeClasses';
import { sanitizeText, sanitizeEmail, validators } from '../utils/sanitize';
import { validateCuit } from '../utils/cuit';
import OnboardingContinueAction from '../components/OnboardingContinueAction';
import { downloadTabularExport } from '../utils/downloadExport';
import { isOperationalWriteDisabled } from '../utils/writeAccess';
import PlatformSubscriberPlanBadge from '../components/PlatformSubscriberPlanBadge';
import { isPlatformSubscriberClient } from '../utils/platformSubscriberClient';

const camposIniciales = {
  razonSocial: "",
  domicilio: "",
  pais: "",
  provincia: "",
  localidad: "",
  email: "",
  telefono: "",
  cuit: "",
  condicionIva: "",
};

function validateClientField(name, value) {
  const trimmed = String(value || '').trim();
  switch (name) {
    case 'razonSocial':
      return trimmed ? '' : 'La razón social es obligatoria.';
    case 'pais':
      return trimmed ? '' : 'El país es obligatorio.';
    case 'email': {
      if (!trimmed) return 'El email es obligatorio.';
      const emailValidation = validators.email(trimmed);
      return emailValidation.valid ? '' : emailValidation.error;
    }
    case 'cuit': {
      const cuitValidation = validateCuit(trimmed, { required: true });
      return cuitValidation.valid ? '' : cuitValidation.error;
    }
    case 'telefono':
      return '';
    default:
      return '';
  }
}

function validateClientForm(values) {
  const fields = ['razonSocial', 'pais', 'email', 'cuit'];
  const errors = {};
  fields.forEach((field) => {
    const message = validateClientField(field, values[field]);
    if (message) errors[field] = message;
  });
  return errors;
}

function initials(name = '') {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return parts.slice(0, 2).map((p) => p[0]).join('').toUpperCase();
}

const Clientes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useContext(AuthContext);
  const { modoOscuro } = useContext(ThemeContext);
  const tc = useThemeClasses();
  const { secureFetch } = useApi();
  const readOnly = isOperationalWriteDisabled(user);

  const initialTab = new URLSearchParams(location.search).get('tab') === 'crear' ? 'crear' : 'listado';
  const [clientes, setClientes] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [filtroPais, setFiltroPais] = useState("");
  const [form, setForm] = useState(camposIniciales);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [offlineMsg, setOfflineMsg] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const fetchClientes = useCallback(async (isInitial = false) => {
    if (isInitial) setInitialLoading(true);
    setLoading(true);
    setError("");
    try {
      const res = await secureFetch("/api/clients");
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || "Error al obtener clientes");
      }
      const data = await res.json();
      setClientes(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Error de conexión");
    } finally {
      setLoading(false);
      if (isInitial) setInitialLoading(false);
    }
  }, [secureFetch]);

  useEffect(() => {
    if (token) fetchClientes(true);
  }, [token, fetchClientes]);

  useEffect(() => {
    if (readOnly && tab !== 'listado') {
      setTab('listado');
      setEditId(null);
      setForm(camposIniciales);
    }
  }, [readOnly, tab]);

  const paises = useMemo(() => {
    const set = new Set();
    clientes.forEach((c) => {
      const p = String(c.pais || '').trim();
      if (p) set.add(p);
    });
    return [...set].sort((a, b) => a.localeCompare(b, 'es'));
  }, [clientes]);

  const clientesFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return clientes
      .filter((c) => {
        if (filtroPais && String(c.pais || '').trim() !== filtroPais) return false;
        if (!q) return true;
        return [c.razonSocial, c.email, c.pais, c.localidad, c.cuit, c.telefono]
          .some((v) => String(v || '').toLowerCase().includes(q));
      })
      .sort((a, b) => String(a.razonSocial || '').localeCompare(String(b.razonSocial || ''), 'es'));
  }, [clientes, busqueda, filtroPais]);

  const kpis = useMemo(() => {
    const conCuit = clientes.filter((c) => String(c.cuit || '').trim()).length;
    const conEmail = clientes.filter((c) => String(c.email || '').trim()).length;
    return {
      total: clientes.length,
      paises: paises.length,
      conCuit,
      conEmail,
    };
  }, [clientes, paises]);

  const showPlanColumn = useMemo(
    () => clientes.some((c) => isPlatformSubscriberClient(c)),
    [clientes],
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    let sanitizedValue = value;
    if (name === 'email') sanitizedValue = sanitizeEmail(value);
    else if (name === 'telefono') sanitizedValue = value.replace(/[^0-9\s()\-+]/g, '');
    else sanitizedValue = sanitizeText(value);
    setForm({ ...form, [name]: sanitizedValue });
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    setError("");
    setSuccess("");
  };

  const handleFieldBlur = (e) => {
    const { name, value } = e.target;
    const message = validateClientField(name, value);
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (message) next[name] = message;
      else delete next[name];
      return next;
    });
    if (name === 'cuit') {
      const cuitValidation = validateCuit(value, { required: true });
      if (cuitValidation.valid && cuitValidation.formatted) {
        setForm((prev) => ({ ...prev, cuit: cuitValidation.formatted }));
      }
    }
  };

  const inputErrorClass = (name) => (
    fieldErrors[name]
      ? modoOscuro
        ? 'border-red-500 focus:ring-red-500/30'
        : 'border-red-500 focus:ring-red-500/30'
      : ''
  );

  const registrarActividad = async (accion, detalles) => {
    try {
      await secureFetch('/api/activity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: accion, details: detalles })
      });
    } catch (err) { /* no bloquear UI */ }
  };

  const obtenerCambiosCliente = (antes, despues) => {
    if (!antes || !despues) return '';
    const campos = [
      { key: 'razonSocial', label: 'Razón social' },
      { key: 'pais', label: 'País' },
      { key: 'localidad', label: 'Localidad' },
      { key: 'email', label: 'Email' },
      { key: 'domicilio', label: 'Domicilio' },
      { key: 'telefono', label: 'Teléfono' },
      { key: 'cuit', label: 'CUIT' },
      { key: 'condicionIva', label: 'IVA' },
    ];
    const cambios = campos
      .filter(({ key }) => (antes[key] || '') !== (despues[key] || ''))
      .map(({ key, label }) => `• ${label}: ${antes[key] || '-'} → ${despues[key] || '-'}`);
    return cambios.length ? cambios.join('\n') : 'Sin cambios relevantes';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextFieldErrors = validateClientForm(form);
    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      setError('Revisá los campos marcados en rojo.');
      return;
    }

    const cuitValidation = validateCuit(form.cuit, { required: true });

    setLoading(true);
    setError("");
    setSuccess("");
    setOfflineMsg("");

    try {
      const sanitizedData = {
        razonSocial: sanitizeText(form.razonSocial),
        domicilio: sanitizeText(form.domicilio),
        pais: sanitizeText(form.pais),
        provincia: sanitizeText(form.provincia),
        localidad: sanitizeText(form.localidad),
        email: sanitizeEmail(form.email),
        telefono: form.telefono.replace(/[^0-9\s()\-+]/g, ''),
        cuit: cuitValidation.formatted || null,
        condicionIva: form.condicionIva || null,
      };

      if (editId) {
        const clienteAntes = clientes.find((c) => c._id === editId);
        const res = await secureFetch(`/api/clients/${editId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(sanitizedData),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "Error al editar cliente");
        setSuccess("Cliente editado correctamente.");
        if (data.offline) setOfflineMsg(data.message || "La acción se guardó offline y se sincronizará cuando vuelvas a tener internet.");
        registrarActividad(
          'Editar cliente',
          `Cliente ${clienteAntes?.razonSocial} editado\n${obtenerCambiosCliente(clienteAntes, sanitizedData)}`
        );
      } else {
        const res = await secureFetch("/api/clients", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(sanitizedData),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "Error al agregar cliente");
        setSuccess("Cliente agregado correctamente.");
        if (data.offline) setOfflineMsg(data.message || "La acción se guardó offline y se sincronizará cuando vuelvas a tener internet.");
        registrarActividad(
          'Crear cliente',
          `Cliente ${sanitizedData.razonSocial} creado\n• Email: ${sanitizedData.email}\n• País: ${sanitizedData.pais}`
        );
      }
      setForm(camposIniciales);
      setFieldErrors({});
      setEditId(null);
      setTab('listado');
      fetchClientes();
    } catch (err) {
      setError(sanitizeText(err.message) || "Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm("¿Seguro que deseas eliminar este cliente?")) return;
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const res = await secureFetch(`/api/clients/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Error al eliminar cliente");
      setSuccess("Cliente eliminado correctamente.");
      if (editId === id) {
        setEditId(null);
        setForm(camposIniciales);
      }
      fetchClientes();
    } catch (err) {
      setError(err.message || "Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (cliente) => {
    setForm({
      ...camposIniciales,
      ...cliente,
      cuit: cliente.cuit || '',
      condicionIva: cliente.condicionIva || '',
    });
    setEditId(cliente._id);
    setFieldErrors({});
    setError("");
    setSuccess("");
    setTab('crear');
  };

  const handleCancelar = () => {
    setForm(camposIniciales);
    setFieldErrors({});
    setEditId(null);
    setError("");
    setSuccess("");
  };

  const exportarClientesExcel = async () => {
    setExportingCsv(true);
    setError("");
    try {
      const name = await downloadTabularExport(
        secureFetch,
        '/api/export/clientes',
        'contsuite-clientes.xlsx'
      );
      setSuccess(`Exportación descargada (${name}).`);
    } catch (err) {
      setError(err.message || 'Error al exportar');
    } finally {
      setExportingCsv(false);
    }
  };

  const openVentas = (cliente) => {
    const nombre = String(cliente?.razonSocial || '').trim();
    if (!nombre) return;
    navigate(`/facturas?cliente=${encodeURIComponent(nombre)}`);
  };

  const box = modoOscuro ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-600';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const tabInactive = modoOscuro ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50';
  const btnOut = modoOscuro ? 'border-gray-600 text-white hover:bg-gray-700' : 'border-gray-300 text-gray-900 hover:bg-gray-50';
  const divide = modoOscuro ? 'border-gray-700' : 'border-gray-200';
  const inputBg = modoOscuro
    ? 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-500'
    : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400';

  if (initialLoading) {
    return (
      <div className={`fixed inset-0 z-50 flex flex-col items-center justify-center ${modoOscuro ? 'bg-black' : 'bg-[#f5f5f7]'}`}>
        <div className="animate-pulse">
          <img src={modoOscuro ? "/logowhite.png" : "/logoblack.png"} alt="Contsuite" className="h-12 w-auto" />
        </div>
        <p className={`mt-8 text-sm font-medium ${textPri}`}>Cargando clientes</p>
        <p className={`text-xs mt-1 ${textSec}`}>Obteniendo tu listado...</p>
      </div>
    );
  }

  return (
    <div className={modoOscuro ? 'w-full min-h-[calc(100dvh-4rem)] bg-black text-white' : 'w-full min-h-[calc(100dvh-4rem)] bg-[#f5f5f7] text-gray-900'}>
      <div className="w-full max-w-[85rem] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-12 py-3 pb-3 space-y-2">
        <header className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <h1 className={`text-xl sm:text-2xl font-semibold leading-tight ${textPri}`}>Clientes</h1>
            <p className={`text-xs ${textSec}`}>Agenda, país y acceso rápido a sus ventas.</p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/clientes-detalle')}
            className={`text-xs font-semibold px-3 py-1.5 rounded-lg border ${btnOut}`}
          >
            Ver ranking
          </button>
        </header>

        <FormAlert
          type="success"
          className="mb-2"
          action={
            <OnboardingContinueAction
              stepId="client"
              visible={Boolean(success && success.includes('agregado'))}
              modoOscuro={modoOscuro}
            />
          }
        >
          {success}
        </FormAlert>
        <FormAlert type="error" className="mb-2">{error}</FormAlert>
        <FormAlert type="warning" className="mb-2">{offlineMsg}</FormAlert>

        <div className={`rounded-xl border p-0.5 ${box}`}>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => { setTab('listado'); setEditId(null); setForm(camposIniciales); }}
              className={`flex-1 px-3 py-1.5 rounded-lg text-sm font-medium ${
                tab === 'listado' ? 'bg-[#1F80FF] text-white' : tabInactive
              }`}
            >
              Listado
            </button>
            {!readOnly && (
              <button
                type="button"
                onClick={() => { setTab('crear'); setEditId(null); setForm(camposIniciales); }}
                className={`flex-1 px-3 py-1.5 rounded-lg text-sm font-medium ${
                  tab === 'crear' ? 'bg-[#1F80FF] text-white' : tabInactive
                }`}
              >
                {editId ? 'Editar' : 'Nuevo'}
              </button>
            )}
          </div>
        </div>

        {tab === 'listado' && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              {[
                { label: 'Clientes', value: String(kpis.total), hint: 'en tu agenda' },
                { label: 'Países', value: String(kpis.paises), hint: 'mercados distintos' },
                { label: 'Con CUIT', value: String(kpis.conCuit), hint: 'identificación fiscal' },
                { label: 'Con email', value: String(kpis.conEmail), hint: 'para recordatorios' },
              ].map((card) => (
                <div key={card.label} className={`rounded-xl border px-3 py-2.5 ${box}`}>
                  <div className={`text-[11px] font-semibold tracking-wide uppercase ${textMuted}`}>{card.label}</div>
                  <div className={`text-base font-semibold ${textPri}`}>{card.value}</div>
                  <div className={`text-[11px] ${textMuted}`}>{card.hint}</div>
                </div>
              ))}
            </div>

            <section className={`rounded-2xl border overflow-hidden ${box}`}>
              <div className={`px-3 py-2 border-b space-y-2 ${divide}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h2 className={`text-sm font-semibold ${textPri}`}>Listado</h2>
                    <p className={`text-xs ${textMuted}`}>
                      {clientesFiltrados.length} de {clientes.length} cliente{clientes.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => { setTab('crear'); setEditId(null); setForm(camposIniciales); }}
                        className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold bg-[#1F80FF] text-white hover:bg-[#1a6fe0]"
                      >
                        Nuevo
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={exportarClientesExcel}
                      disabled={exportingCsv}
                      className={`inline-flex items-center px-3 py-1.5 rounded-md border text-xs font-semibold disabled:opacity-50 ${btnOut}`}
                    >
                      {exportingCsv ? 'Exportando…' : 'Excel'}
                    </button>
                  </div>
                </div>

                <div className="relative">
                  <input
                    type="search"
                    placeholder="Buscar por razón social, email, CUIT, país o teléfono"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    className={`w-full pl-3 pr-3 py-2 rounded-lg border text-sm ${inputBg} focus:outline-none focus:ring-2 focus:ring-[#1F80FF]/30`}
                  />
                </div>

                {paises.length > 1 && (
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      onClick={() => setFiltroPais('')}
                      className={`px-2.5 py-1 rounded-md text-[11px] font-semibold ${
                        !filtroPais ? 'bg-[#1F80FF] text-white' : modoOscuro ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-600 border border-gray-200'
                      }`}
                    >
                      Todos
                    </button>
                    {paises.map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setFiltroPais(p === filtroPais ? '' : p)}
                        className={`px-2.5 py-1 rounded-md text-[11px] font-semibold ${
                          filtroPais === p ? 'bg-[#1F80FF] text-white' : modoOscuro ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-600 border border-gray-200'
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {loading && clientes.length === 0 ? (
                <div className="p-4 sm:p-5 space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className={`h-16 rounded-xl animate-pulse ${modoOscuro ? 'bg-gray-800' : 'bg-gray-100'}`} />
                  ))}
                </div>
              ) : clientesFiltrados.length === 0 ? (
                <div className="px-4 py-12 text-center">
                  <p className={`text-sm font-medium ${textPri}`}>
                    {busqueda || filtroPais ? 'No hay resultados para este recorte' : 'Todavía no hay clientes'}
                  </p>
                  <p className={`text-xs mt-1 ${textMuted}`}>
                    {busqueda || filtroPais
                      ? 'Probá otro país o limpiá la búsqueda.'
                      : 'Cargá el primero para facturarle después.'}
                  </p>
                  {!busqueda && !filtroPais && (
                    <button
                      type="button"
                      onClick={() => setTab('crear')}
                      className="mt-4 inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold bg-[#1F80FF] text-white"
                    >
                      Agregar cliente
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-2 p-3 md:hidden">
                    {clientesFiltrados.map((c) => (
                      <div key={c._id} className={`rounded-xl border p-3 ${modoOscuro ? 'border-gray-800 bg-gray-800/40' : 'border-gray-100 bg-gray-50'}`}>
                        <div className="flex items-start gap-3">
                          <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-[11px] font-semibold shrink-0 ${
                            modoOscuro ? 'bg-[#1F80FF]/20 text-[#5CA3FF]' : 'bg-[#1F80FF]/10 text-[#1F80FF]'
                          }`}>
                            {initials(c.razonSocial)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <button type="button" onClick={() => openVentas(c)} className={`text-sm font-semibold text-left ${textPri}`}>
                              {c.razonSocial}
                            </button>
                            <p className={`text-[11px] mt-0.5 ${textMuted}`}>
                              {[c.pais, c.localidad].filter(Boolean).join(' · ') || 'Sin ubicación'}
                            </p>
                            <p className={`text-[11px] truncate ${textSec}`}>{c.email || 'Sin email'}</p>
                            {c.cuit && <p className={`text-[11px] ${textMuted}`}>CUIT {c.cuit}</p>}
                            {showPlanColumn && (
                              <PlatformSubscriberPlanBadge client={c} modoOscuro={modoOscuro} compact />
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <button type="button" onClick={() => openVentas(c)} className={`flex-1 py-1.5 rounded-md border text-[11px] font-semibold ${btnOut}`}>Ventas</button>
                          {!readOnly && (
                            <>
                              <button type="button" onClick={() => handleEditar(c)} className={`flex-1 py-1.5 rounded-md border text-[11px] font-semibold ${btnOut}`}>Editar</button>
                              <button type="button" onClick={() => handleEliminar(c._id)} className={`flex-1 py-1.5 rounded-md border text-[11px] font-semibold ${btnOut}`}>Eliminar</button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="overflow-x-auto hidden md:block">
                    <table className="w-full min-w-[780px]">
                      <thead>
                        <tr className={`text-[11px] uppercase tracking-wide ${textMuted} ${modoOscuro ? 'bg-gray-800/60' : 'bg-gray-50'}`}>
                          <th className="text-left font-semibold py-2.5 px-4">Cliente</th>
                          <th className="text-left font-semibold py-2.5 px-3">Ubicación</th>
                          <th className="text-left font-semibold py-2.5 px-3">Contacto</th>
                          <th className="text-left font-semibold py-2.5 px-3">Fiscal</th>
                          {showPlanColumn && (
                            <th className="text-left font-semibold py-2.5 px-3">Plan</th>
                          )}
                          <th className="text-right font-semibold py-2.5 px-4">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientesFiltrados.map((c) => (
                          <tr
                            key={c._id}
                            className={`border-t ${modoOscuro ? 'border-gray-800 hover:bg-gray-800/70' : 'border-gray-100 hover:bg-gray-50'}`}
                          >
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-3 min-w-0">
                                <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-[11px] font-semibold shrink-0 ${
                                  modoOscuro ? 'bg-[#1F80FF]/20 text-[#5CA3FF]' : 'bg-[#1F80FF]/10 text-[#1F80FF]'
                                }`}>
                                  {initials(c.razonSocial)}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => openVentas(c)}
                                  className={`text-sm font-semibold text-left truncate hover:text-[#1F80FF] ${textPri}`}
                                >
                                  {c.razonSocial}
                                </button>
                              </div>
                            </td>
                            <td className={`py-3 px-3 text-sm ${textSec}`}>
                              <div>{c.pais || '—'}</div>
                              <div className={`text-[11px] ${textMuted}`}>{c.localidad || c.domicilio || '—'}</div>
                            </td>
                            <td className={`py-3 px-3 text-sm ${textSec}`}>
                              <div className="truncate max-w-[220px]">{c.email || '—'}</div>
                              <div className={`text-[11px] ${textMuted}`}>{c.telefono || 'Sin teléfono'}</div>
                            </td>
                            <td className={`py-3 px-3 text-sm ${textSec}`}>
                              <div>{c.cuit || 'Sin CUIT'}</div>
                              <div className={`text-[11px] ${textMuted}`}>
                                {isPlatformSubscriberClient(c)
                                  ? (c.metadata?.platformFiscalLabel || 'Suscriptor plataforma')
                                  : (c.condicionIva || 'IVA no informado')}
                              </div>
                            </td>
                            {showPlanColumn && (
                              <td className="py-3 px-3 text-sm">
                                {isPlatformSubscriberClient(c) ? (
                                  <PlatformSubscriberPlanBadge client={c} modoOscuro={modoOscuro} />
                                ) : (
                                  <span className={textMuted}>—</span>
                                )}
                              </td>
                            )}
                            <td className="py-3 px-4 text-right">
                              <div className="inline-flex items-center gap-1.5">
                                <button type="button" onClick={() => openVentas(c)} className={`px-2.5 py-1 rounded-md border text-[11px] font-semibold ${btnOut}`}>Ventas</button>
                                {!readOnly && (
                                  <>
                                    <button type="button" onClick={() => handleEditar(c)} className={`px-2.5 py-1 rounded-md border text-[11px] font-semibold ${btnOut}`}>Editar</button>
                                    <button type="button" onClick={() => handleEliminar(c._id)} className={`px-2.5 py-1 rounded-md border text-[11px] font-semibold ${btnOut}`}>Eliminar</button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </section>
          </>
        )}

        {!readOnly && tab === 'crear' && (
          <FormShell
            box={box}
            divide={divide}
            textPri={textPri}
            textMuted={textMuted}
            title={editId ? 'Editar cliente' : 'Nuevo cliente'}
            subtitle={editId ? 'Actualizá los datos.' : 'Los campos con * son obligatorios. El CUIT se valida al salir del campo.'}
            headerClassName="px-4 py-2.5"
            bodyClassName="p-4"
            titleClassName="text-sm"
            iconWrapClassName="w-8 h-8"
            icon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v12m6-6H6" />
              </svg>
            }
          >
            <form className="space-y-3" onSubmit={handleSubmit} autoComplete="off" noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-2.5 items-start">
                <FormField label="Razón social" required error={fieldErrors.razonSocial} labelClass={tc.labelClass}>
                  <input name="razonSocial" className={`${formInputClass(tc.inputClass, { compact: true })} ${inputErrorClass('razonSocial')}`} value={form.razonSocial} onChange={handleChange} onBlur={handleFieldBlur} placeholder="Nombre de la empresa o cliente" required />
                </FormField>
                <div className="sm:col-span-2">
                  <CountrySubdivisionSelect
                    countryName={form.pais}
                    provinceName={form.provincia}
                    onCountryChange={(name) => {
                      setForm((prev) => ({ ...prev, pais: name, provincia: '' }));
                      if (fieldErrors.pais) {
                        setFieldErrors((prev) => ({ ...prev, pais: validateClientField('pais', name) }));
                      }
                    }}
                    onProvinceChange={(name) => setForm((prev) => ({ ...prev, provincia: name }))}
                    countryError={fieldErrors.pais}
                    countryRequired
                    tc={tc}
                    compact
                  />
                </div>
                <FormField label="Email" required error={fieldErrors.email} labelClass={tc.labelClass}>
                  <input name="email" type="email" className={`${formInputClass(tc.inputClass, { compact: true })} ${inputErrorClass('email')}`} value={form.email} onChange={handleChange} onBlur={handleFieldBlur} placeholder="correo@ejemplo.com" required />
                </FormField>
                <PhoneCountryInput
                  value={form.telefono}
                  onChange={(telefono) => {
                    setForm((prev) => ({ ...prev, telefono }));
                    setError('');
                    setSuccess('');
                  }}
                  countryName={form.pais}
                  defaultCountryCode="AR"
                  tc={tc}
                  compact
                  className="sm:col-span-2 lg:col-span-2"
                />
                <FormField label="CUIT" required error={fieldErrors.cuit} labelClass={tc.labelClass}>
                  <input
                    name="cuit"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    className={`${formInputClass(tc.inputClass, { compact: true })} ${inputErrorClass('cuit')}`}
                    value={form.cuit}
                    onChange={handleChange}
                    onBlur={handleFieldBlur}
                    placeholder="20-12345678-9"
                    aria-invalid={fieldErrors.cuit ? 'true' : undefined}
                  />
                </FormField>
                <FormField label="Condición IVA" labelClass={tc.labelClass}>
                  <select name="condicionIva" className={formInputClass(tc.inputClass, { compact: true })} value={form.condicionIva} onChange={handleChange}>
                    <option value="">Sin especificar</option>
                    <option value="CF">Consumidor final</option>
                    <option value="RI">Responsable inscripto</option>
                    <option value="Monotributo">Monotributo</option>
                    <option value="Exento">Exento</option>
                  </select>
                </FormField>
                <FormField label="Localidad" labelClass={tc.labelClass}>
                  <input name="localidad" className={formInputClass(tc.inputClass, { compact: true })} value={form.localidad} onChange={handleChange} placeholder="Ciudad o localidad" />
                </FormField>
                <FormField label="Domicilio" labelClass={tc.labelClass}>
                  <input name="domicilio" className={formInputClass(tc.inputClass, { compact: true })} value={form.domicilio} onChange={handleChange} placeholder="Dirección completa" />
                </FormField>
              </div>

              <FormActions
                submitLabel={editId ? 'Guardar cambios' : 'Agregar cliente'}
                onCancel={editId ? handleCancelar : () => setTab('listado')}
                showCancel
                loading={loading}
                btnPrimary={tc.btnPrimary}
                btnOutline={tc.btnOutline}
              />
            </form>
          </FormShell>
        )}
      </div>
    </div>
  );
};

export default Clientes;
